import { useId, useState } from "react";
import "./PickerGrid.css";

interface PickerItem {
  id: string;
  label: string;
}

interface PickerGridProps {
  legendId: string;
  items: PickerItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /**
   * Collapse to this many items with a "show more" expander when the list is
   * longer than it. Omitted entirely (the payment-method picker, which never
   * runs long enough to need this) means always render everything.
   *
   * fc-collapse (quick-entry scroll reduction): 8 vendors / 19 categories
   * rendered flat put the Amount step ~1.85 screens down. Collapsing to the
   * ~6 most-used (see `lib/usage.ts`) gets a common pick to the same 8 taps
   * as before; only a less-common choice costs the one extra tap to expand.
   */
  initialVisibleCount?: number;
  /**
   * Plural noun for the expander's accessible name — "Show 13 more
   * categories", not just "Show 13 more". Two collapsed pickers can sit on
   * the same screen; without this they read as identical controls to a
   * screen-reader user. Required whenever `initialVisibleCount` is set.
   */
  itemNamePlural?: string;
  /**
   * The same noun in the singular, used only when exactly ONE item is hidden.
   *
   * Without it the expander reads "Show 1 more vendors". Measured 2026-09-07 by rendering
   * the picker with seven vendors — the demo tenants carry eight, so the collapse leaves two
   * and this branch had never been on screen. Explicit rather than derived: "categories" to
   * "category" is not a rule worth writing, and this component's whole style is to be told
   * rather than to guess.
   */
  itemNameSingular?: string;
}

/**
 * A wrapping row of chips for a single-choice pick-list. Used for vendor,
 * category and payment-method selection on the phone quick-entry flow —
 * deliberately not a <select>, which is small-target and slow to operate
 * one-handed on a phone, and not free text, per the constraint that the
 * visitor flow uses constrained inputs only.
 *
 * Text-only by design: the per-item `icon` prop is gone along with the emoji
 * map that fed it (see lib/category.ts for why). A chip is a word, and a word
 * is unambiguous at any size on any platform.
 *
 * Deliberately plain <button> elements with aria-pressed rather than
 * role="radio"/"radiogroup": ARIA radio semantics obligate the author to
 * implement roving-tabindex arrow-key navigation (WAI-ARIA APG), which this
 * component does not do. Native buttons are individually Tab-reachable and
 * Enter/Space-activatable with zero extra JS, which is a true (if less
 * idiomatic) guarantee rather than a role promising behavior that isn't
 * implemented — see persona standard "keyboard alone" operability.
 */
export function PickerGrid({
  legendId,
  items,
  selectedId,
  onSelect,
  initialVisibleCount,
  itemNamePlural,
  itemNameSingular,
}: PickerGridProps) {
  const [expanded, setExpanded] = useState(false);
  const gridId = useId();

  const hasCollapse = initialVisibleCount != null && items.length > initialVisibleCount;
  const visibleItems = hasCollapse && !expanded ? items.slice(0, initialVisibleCount) : items;
  const hiddenCount = items.length - visibleItems.length;
  const plural = itemNamePlural ?? "options";
  const singular = itemNameSingular ?? plural;

  return (
    <>
      <div id={gridId} className="picker-grid" role="group" aria-labelledby={legendId}>
        {visibleItems.map((item) => {
          const selected = item.id === selectedId;
          return (
            <button
              key={item.id}
              type="button"
              aria-pressed={selected}
              className={`picker-grid__item${selected ? " picker-grid__item--selected" : ""}`}
              onClick={() => onSelect(item.id)}
            >
              <span className="picker-grid__label">{item.label}</span>
            </button>
          );
        })}
      </div>
      {/*
        Sits OUTSIDE the role="group" div on purpose — it toggles what the
        group contains, it is not itself one of the group's choices. Plain
        <button> + aria-expanded rather than a details/summary disclosure:
        the group above needs full control of its own layout (chip wrapping),
        which a native <details> content box does not compose with cleanly.
      */}
      {hasCollapse && (
        <button
          type="button"
          className="picker-grid__more"
          aria-expanded={expanded}
          aria-controls={gridId}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded
            ? `Show fewer ${plural}`
            : `Show ${hiddenCount} more ${hiddenCount === 1 ? singular : plural}`}
        </button>
      )}
    </>
  );
}
